import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import DocumentScannerSharpIcon from "@mui/icons-material/DocumentScannerSharp";
import EditNoteSharpIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import "./PaymentInvoiceProcurement.css";
import DeleteIcon from "@mui/icons-material/Delete";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { NumericFormat } from "react-number-format";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from "@mui/icons-material/AttachFile";

const PaymentInvoiceProcurement = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const [existingAttachments, setExistingAttachments] = useState([]);
    const [loadingAttachments, setLoadingAttachments] = useState(false);

    const MEDIA_BASE_URL = process.env.REACT_APP_API_URL
        ? `${process.env.REACT_APP_API_URL}/media/`
        : "http://localhost:8000/media/";

    const [formData, setFormData] = useState({
        project_code: "",
        project_code_display: "",
        subproject_code: "",
        subproject_code_display: "",
        cost_id: "",
        cost_display: "",
        supplier: "",
        supplier_display: "",
        invoice_number: "",
        invoice_date: "",
        bill_maturity: "",
        invoice_without_vat: "",
        supplier_vat: "21",
        total_invoice_with_vat: "",
        currency: "",
        currency_display: "",
        exchange_rate: "",
        exchange_rate_value: "",
        invoice_without_vat_system: "",
        total_invoice_with_vat_system: "",
        remaining_payment_system: "",
        remaining_payment_system_display: "",
        medium_alert: "5",
        critical_alert: "1",
        remarks: "",
        month: "",
        year: "",
        payments_status: "Unpaid",
        contract: "",
        contract_display: "",
        purchase_order: "",
        purchase_order_display: "",
        system_currency_display: "System",
        // Campuri pentru Credit Note
        original_invoice: "",
        original_invoice_display: "",
        credit_note_without_vat: "",
        credit_note_with_vat: "",
        credit_note_without_vat_system: "",
        credit_note_with_vat_system: "",
        difference_without_vat: "",
        difference_with_vat: "",
        difference_without_vat_system: "",
        difference_with_vat_system: "",
    });

    const getFileUrl = (file) => {
        if (!file) return null;

        const possibleUrl = file.file_url || file.url || file.file || "";

        if (!possibleUrl) return null;

        if (
            possibleUrl.startsWith("http://") ||
            possibleUrl.startsWith("https://") ||
            possibleUrl.startsWith("blob:")
        ) {
            return possibleUrl;
        }

        const cleanPath = possibleUrl.replace(/^\/?media\//, "");
        return `${MEDIA_BASE_URL}${cleanPath}`;
    };

    const handleFileClick = (file) => {
        const fileUrl = getFileUrl(file);

        if (!fileUrl) {
            alert("File URL not available");
            return;
        }

        const filename = file.filename || (file.file ? file.file.split("/").pop() : "file");
        const extension = filename.split(".").pop()?.toLowerCase() || "";

        if (extension === "pdf") {
            window.open(fileUrl, "_blank");
            return;
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const getFileIcon = (file) => {
        const filename = file.filename || "";
        const extension = filename.split(".").pop()?.toLowerCase() || "";

        if (extension === "pdf") {
            return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
        }

        if (["doc", "docx"].includes(extension)) {
            return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
        }

        if (["xls", "xlsx"].includes(extension)) {
            return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
        }

        if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
            return <InsertDriveFileIcon style={{ fontSize: 24, color: "#9c27b0" }} />;
        }

        return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
    };

    const formatNumber = (value, locale = "ro-RO") => {
        if (value === null || value === undefined || value === "") return "";

        const n = Number(value);
        if (Number.isNaN(n)) return "";

        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n);
    };

    const fetchAttachmentDetails = useCallback(async (attachmentId) => {
        try {
            const response = await axiosInstance.get(
                `/invoice-procurement-attach/${attachmentId}/`
            );
            return response.data;
        } catch (error) {
            console.error(`Error fetching attachment ${attachmentId}:`, error);
            return null;
        }
    }, []);

    const fetchAttachments = useCallback(async () => {
        if (!id) return;

        setLoadingAttachments(true);

        try {
            const response = await axiosInstance.get(`/invoice-procurement/${id}/`);
            const data = response.data;

            if (data.attach_files && Array.isArray(data.attach_files)) {
                setExistingAttachments(
                    data.attach_files.map((att) => ({
                        id: att.id,
                        filename: att.filename || (att.file ? att.file.split("/").pop() : "Unknown file"),
                        file: att.file,
                        file_url: att.file_url || att.url || null,
                        file_size: att.file_size || att.size || 0,
                        uploaded_at: att.uploaded_at,
                    }))
                );
            } else if (
                data.attach &&
                Array.isArray(data.attach) &&
                data.attach.length > 0 &&
                typeof data.attach[0] === "number"
            ) {
                const attachmentPromises = data.attach.map((attId) =>
                    fetchAttachmentDetails(attId)
                );
                const attachments = await Promise.all(attachmentPromises);
                setExistingAttachments(attachments.filter((att) => att !== null));
            } else if (data.attach && Array.isArray(data.attach)) {
                setExistingAttachments(
                    data.attach.map((att) => ({
                        id: att.id,
                        filename: att.filename || (att.file ? att.file.split("/").pop() : "Unknown file"),
                        file: att.file,
                        file_url: att.file_url || att.url || null,
                        file_size: att.file_size || att.size || 0,
                        uploaded_at: att.uploaded_at,
                    }))
                );
            } else {
                setExistingAttachments([]);
            }
        } catch (error) {
            console.error("Error fetching attachments:", error);
        } finally {
            setLoadingAttachments(false);
        }
    }, [id, fetchAttachmentDetails]);

    useEffect(() => {
        const loadInvoiceData = async () => {
            if (!id) return;

            try {
                const response = await axiosInstance.get(`/invoice-procurement/${id}/`);
                const data = response.data;

                setFormData({
                    project_code: data.project_code || "",
                    project_code_display: data.project_code_display || "-",
                    subproject_code: data.subproject_code || "",
                    subproject_code_display: data.subproject_code_display || "-",
                    cost_id: data.cost_id || "",
                    cost_display: data.cost_display || "-",
                    supplier: data.supplier || "",
                    supplier_display: data.supplier_display || "N/A",
                    invoice_number: data.invoice_number || "",
                    invoice_date: data.invoice_date || "",
                    bill_maturity: data.bill_maturity || "",
                    invoice_without_vat: data.invoice_without_vat || "",
                    supplier_vat: data.supplier_vat || "21",
                    total_invoice_with_vat: data.total_invoice_with_vat || "",
                    currency: data.currency || "",
                    currency_display: data.currency_display || "-",
                    exchange_rate: data.exchange_rate || "",
                    exchange_rate_value: data.exchange_rate_value || "",
                    invoice_without_vat_system: data.invoice_without_vat_system || "",
                    total_invoice_with_vat_system: data.total_invoice_with_vat_system || "",
                    remaining_payment_system: data.remaining_payment_system || "",
                    remaining_payment_system_display: data.remaining_payment_system_display || "",
                    medium_alert: data.medium_alert || "5",
                    critical_alert: data.critical_alert || "1",
                    remarks: data.remarks || "",
                    month: data.month || "",
                    year: data.year || "",
                    payments_status: data.payments_status || "Unpaid",
                    contract: data.contract || "",
                    contract_display: data.contract_display || "-",
                    purchase_order: data.purchase_order || "",
                    purchase_order_display: data.purchase_order_display || "-",
                    system_currency_display: data.system_currency_display || "System",
                    // Campuri Credit Note
                    original_invoice: data.original_invoice || "",
                    original_invoice_display: data.original_invoice_display || "-",
                    credit_note_without_vat: data.credit_note_without_vat || "",
                    credit_note_with_vat: data.credit_note_with_vat || "",
                    credit_note_without_vat_system: data.credit_note_without_vat_system || "",
                    credit_note_with_vat_system: data.credit_note_with_vat_system || "",
                    difference_without_vat: data.difference_without_vat || "",
                    difference_with_vat: data.difference_with_vat || "",
                    difference_without_vat_system: data.difference_without_vat_system || "",
                    difference_with_vat_system: data.difference_with_vat_system || "",
                });

                fetchAttachments();
            } catch (error) {
                console.error("Error loading invoice:", error);
            }
        };

        loadInvoiceData();
    }, [id, fetchAttachments]);

    const invoiceCurrencyLabel = useMemo(() => {
        return formData.currency_display || "-";
    }, [formData.currency_display]);

    const getExchangeRateValue = useCallback(() => {
        const rateValue = Number(formData.exchange_rate_value);
        if (Number.isFinite(rateValue) && rateValue > 0) {
            return rateValue;
        }
        return null;
    }, [formData.exchange_rate_value]);

    const convertToSystemAmount = useCallback(
        (value) => {
            const numericValue = Number(value);
            const rateValue = getExchangeRateValue();

            if (!Number.isFinite(numericValue) || rateValue === null) return "";

            return (numericValue * rateValue).toFixed(2);
        },
        [getExchangeRateValue]
    );

    const formatMoney = useCallback((value, currencyLabel = "") => {
        if (value === null || value === undefined || value === "") return "-";

        const formattedValue = formatNumber(value);
        if (!formattedValue) return "-";

        return `${formattedValue}${currencyLabel ? ` ${currencyLabel}` : ""}`;
    }, []);

    const formatConvertedMoney = useCallback(
        (originalValue, systemValueFromApi = "") => {
            const original = formatMoney(originalValue, invoiceCurrencyLabel);

            const systemValue =
                systemValueFromApi !== null &&
                    systemValueFromApi !== undefined &&
                    systemValueFromApi !== ""
                    ? systemValueFromApi
                    : convertToSystemAmount(originalValue);

            if (!systemValue || original === "-") return original;

            return `${original} → ${formatMoney(systemValue, formData.system_currency_display || "System")}`;
        },
        [convertToSystemAmount, formatMoney, invoiceCurrencyLabel, formData.system_currency_display]
    );

    const statusColors = {
        "Partially paid": "#ff9800",
        Paid: "#4caf50",
        Unpaid: "#f44336",
    };

    const [openModal, setOpenModal] = useState(false);

    const handleAddClick = () => {
        setFormDataPayment({
            invoice_id: id,
            invoice_number: formData.invoice_number || "",
            supplier_payment: "",
            invoice_pay_date: "",
            invoice_pay_date_display: "",
            remaining_payment: "",
        });

        setFieldErrors({});
        setOpenModal(true);
    };

    const [isEditMode, setIsEditMode] = useState(false);
    const [currentEditId, setCurrentEditId] = useState(null);

    const handleDeleteClick = (payment) => {
        setItemToDelete(payment);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await axiosInstance.delete(
                `/payment-history-invoice-procurement/${itemToDelete.id}/`
            );
            fetchPaymentsForInvoice();
            setOpenDeleteDialog(false);
        } catch (error) {
            console.error("Error deleting payment:", error.response?.data || error.message);
        }
    };

    const [formDataPayment, setFormDataPayment] = useState({
        invoice_id: "",
        invoice_number: "",
        supplier_payment: "",
        invoice_pay_date: "",
        invoice_pay_date_display: "",
        remaining_payment: "",
    });

    const handleDatePick = (field, date) => {
        if (!date) {
            setFormDataPayment((prev) => ({
                ...prev,
                [field]: "",
                [field + "_display"]: "",
            }));
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setFormDataPayment((prev) => ({
            ...prev,
            [field]: iso,
            [field + "_display"]: display,
        }));

        if (fieldErrors.invoice_pay_date) {
            setFieldErrors((prev) => ({ ...prev, invoice_pay_date: "" }));
        }
    };

    const validatePaymentForm = () => {
        const newErrors = {};
        const remaining = parseFloat(remainingAmount) || 0;
        const supplierPayment = parseFloat(formDataPayment.supplier_payment) || 0;

        if (!formDataPayment.invoice_pay_date) {
            newErrors.invoice_pay_date = "Required";
        }

        if (!formDataPayment.supplier_payment || formDataPayment.supplier_payment === "") {
            newErrors.supplier_payment = "Required";
        } else if (supplierPayment <= 0) {
            newErrors.supplier_payment = "Supplier Payment must be greater than 0";
        } else if (supplierPayment > remaining) {
            newErrors.supplier_payment = `Payment exceeds remaining amount (${formatNumber(
                remaining
            )} ${invoiceCurrencyLabel || ""}).`;
        }

        setFieldErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validatePaymentForm()) return;

        setIsSaving(true);

        try {
            const payload = {
                invoice: formDataPayment.invoice_id,
                supplier_payment: parseFloat(formDataPayment.supplier_payment),
                invoice_pay_date: formDataPayment.invoice_pay_date,
                remaining_payment: formDataPayment.remaining_payment || 0,
            };

            if (isEditMode && currentEditId) {
                await axiosInstance.put(
                    `/payment-history-invoice-procurement/${currentEditId}/`,
                    payload
                );
            } else {
                await axiosInstance.post("/payment-history-invoice-procurement/", payload);
            }

            setShowToast(true);
            fetchPaymentsForInvoice();

            setTimeout(() => {
                handleCloseModal();
                setIsSaving(false);
                window.location.reload();
            }, 700);
        } catch (error) {
            console.error("Submit error:", error.response?.data || error.message);
            setIsSaving(false);
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEditMode(false);
        setCurrentEditId(null);
        setWarningMessage("");
        setFieldErrors({});

        setFormDataPayment({
            invoice_id: "",
            supplier_payment: "",
            invoice_pay_date: "",
            invoice_pay_date_display: "",
            remaining_payment: "",
        });
    };

    const [invoicePayments, setInvoicePayments] = useState([]);

    const fetchPaymentsForInvoice = useCallback(async () => {
        if (!id) return;

        try {
            const response = await axiosInstance.get(
                `/payment-history-invoice-procurement/?invoice=${id}`
            );

            const payments = response.data;

            const filteredPayments = payments.filter(
                (payment) => payment.invoice === parseInt(id)
            );

            setInvoicePayments(filteredPayments);
        } catch (error) {
            console.error("Error fetching payments:", error);
        }
    }, [id]);

    const [warningMessage, setWarningMessage] = useState("");

    const handleChange = (e) => {
        const { name, value } = e.target;
        const numericFields = ["supplier_payment", "remaining_payment"];

        const updatedValue = numericFields.includes(name)
            ? value === ""
                ? ""
                : parseFloat(value)
            : value;

        const updatedFormData = {
            ...formDataPayment,
            [name]: updatedValue,
        };

        if (name === "supplier_payment") {
            const supplierPayment = updatedValue || 0;
            const totalInvoice = parseFloat(formData.total_invoice_with_vat) || 0;

            let totalPaidExcludingCurrent = 0;

            invoicePayments.forEach((payment) => {
                const amount = parseFloat(payment.supplier_payment);

                if (!isNaN(amount)) {
                    if (isEditMode && currentEditId && payment.id === currentEditId) {
                        return;
                    }

                    totalPaidExcludingCurrent += amount;
                }
            });

            const remaining = totalInvoice - totalPaidExcludingCurrent - supplierPayment;

            updatedFormData.remaining_payment = remaining.toFixed(2);

            if (remaining < 0) {
                setWarningMessage("⚠️ Payment cannot exceed the total invoice amount.");
            } else {
                setWarningMessage("");
            }
        }

        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({ ...prev, [name]: "" }));
        }

        setFormDataPayment(updatedFormData);
    };

    useEffect(() => {
        if (id) {
            fetchPaymentsForInvoice();
        }
    }, [id, fetchPaymentsForInvoice]);

    const handleEditClick = (payment) => {
        const displayDate = payment.invoice_pay_date
            ? format(new Date(payment.invoice_pay_date), "dd.MM.yyyy")
            : "";

        setFormDataPayment({
            invoice_id: payment.invoice,
            supplier_payment: payment.supplier_payment,
            invoice_pay_date: payment.invoice_pay_date,
            invoice_pay_date_display: displayDate,
            remaining_payment: payment.remaining_payment,
        });

        setCurrentEditId(payment.id);
        setIsEditMode(true);
        setFieldErrors({});
        setOpenModal(true);
    };

    const calculateSupplierPaymentTotal = (payments) => {
        return payments.reduce((total, payment) => {
            const supplierPayment = parseFloat(payment.supplier_payment);
            return !isNaN(supplierPayment) ? total + supplierPayment : total;
        }, 0);
    };

    const calculateRemainingAmount = (payments, totalInvoiceWithVat) => {
        const totalPaid = calculateSupplierPaymentTotal(payments);
        const totalInvoice = parseFloat(totalInvoiceWithVat) || 0;
        const remainingAmount = totalInvoice - totalPaid;

        return remainingAmount.toFixed(2);
    };

    const remainingAmount = calculateRemainingAmount(
        invoicePayments,
        formData.total_invoice_with_vat
    );

    const [canEditInvoice, setCanEditInvoice] = useState(false);
    const [canCreatePayment, setCanCreatePayment] = useState(false);
    const [canEditPayment, setCanEditPayment] = useState(false);
    const [canDeletePayment, setCanDeletePayment] = useState(false);

    useEffect(() => {
        const requiredPathInvoice = "/invoice-procurement";
        const requiredPathPayment = "/invoice-procurement/payment-invoice-procurement";
        const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
        const permissions = storedPermissions?.pages || [];

        setCanEditInvoice(
            permissions.some(
                (page) => new URL(page.url).pathname === requiredPathInvoice && page.can_edit
            )
        );

        setCanCreatePayment(
            permissions.some(
                (page) => new URL(page.url).pathname === requiredPathPayment && page.can_create
            )
        );

        setCanEditPayment(
            permissions.some(
                (page) => new URL(page.url).pathname === requiredPathPayment && page.can_edit
            )
        );

        setCanDeletePayment(
            permissions.some(
                (page) => new URL(page.url).pathname === requiredPathPayment && page.can_delete
            )
        );
    }, []);

    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [showToast]);

    const errorStyle = {
        color: "#d32f2f",
        fontSize: "12px",
        marginTop: "2px",
        display: "block",
    };

    const ErrorMessage = ({ error }) => {
        if (!error) return null;

        return <div style={errorStyle}>{error}</div>;
    };

    // Verificam daca este Credit Note
    const isCreditNote = formData.original_invoice && formData.original_invoice !== "";

    return (
        <div className="payment-ext-resource-invoice-page">
            {showToast &&
                createPortal(
                    <div className="global-toast-on-top">
                        {isEditMode
                            ? "✔ Payment updated successfully!"
                            : "✔ Payment added successfully!"}
                    </div>,
                    document.body
                )}

            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                className="add-payment-container"
            >
                <DialogTitle className="title-popup">
                    {isEditMode ? "Edit Payment" : "Add New Payment"}
                </DialogTitle>

                <DialogContent dividers className="payment-ext-resource-invoice-page">
                    <div className="form-group">
                        <label htmlFor="invoice" className="label">
                            Invoice
                        </label>

                        <input
                            type="text"
                            id="invoice"
                            name="invoice"
                            value={formData.invoice_number || ""}
                            className="input"
                            disabled
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="invoice_pay_date" className="label">
                            Invoice Pay Date <span style={{ color: "red" }}>*</span>
                        </label>

                        <div className="datepicker-container">
                            <input
                                type="text"
                                value={formDataPayment.invoice_pay_date_display || ""}
                                placeholder="dd.mm.yyyy"
                                className={`input ${fieldErrors.invoice_pay_date ? "input-error" : ""
                                    }`}
                                readOnly
                                onClick={() =>
                                    document.querySelector("#invoice_pay_date_picker")?.focus()
                                }
                                style={{
                                    borderColor: fieldErrors.invoice_pay_date ? "#d32f2f" : "",
                                }}
                            />

                            <DatePicker
                                id="invoice_pay_date_picker"
                                selected={
                                    formDataPayment.invoice_pay_date
                                        ? new Date(formDataPayment.invoice_pay_date)
                                        : null
                                }
                                onChange={(date) => handleDatePick("invoice_pay_date", date)}
                                dateFormat="dd.MM.yyyy"
                                className="datepicker-hidden"
                                autoComplete="off"
                                popperClassName="datepicker-popper-custom"
                                popperProps={{
                                    strategy: "fixed",
                                }}
                                calendarClassName="datepicker-calendar-custom"
                            />

                            <span
                                className="calendar-icon-inside"
                                onClick={() =>
                                    document.querySelector("#invoice_pay_date_picker")?.focus()
                                }
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                            </span>
                        </div>

                        <ErrorMessage error={fieldErrors.invoice_pay_date} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="supplier_payment" className="label">
                            Supplier Payment{" "}
                            {invoiceCurrencyLabel ? `(${invoiceCurrencyLabel})` : ""}
                            <span style={{ color: "red" }}> *</span>
                        </label>

                        <NumericFormat
                            id="supplier_payment"
                            name="supplier_payment"
                            value={formDataPayment.supplier_payment ?? ""}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            customInput="input"
                            className="input"
                            style={{
                                borderColor: fieldErrors.supplier_payment ? "#d32f2f" : "",
                            }}
                            required
                            onValueChange={(values) =>
                                handleChange({
                                    target: {
                                        name: "supplier_payment",
                                        value: values.floatValue ?? 0,
                                    },
                                })
                            }
                        />

                        <ErrorMessage error={fieldErrors.supplier_payment} />
                    </div>

                    <div className="form-group">
                        {warningMessage && (
                            <div style={{ color: "red", marginTop: "0.5rem" }}>
                                {warningMessage}
                            </div>
                        )}
                    </div>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleCloseModal}
                        color="secondary"
                        className="button discard"
                    >
                        Discard
                    </Button>

                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        className={`button save ${isSaving ? "disabled" : ""}`}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>

                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this payment?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        sx={{
                            backgroundColor: "#e0e0e0",
                            color: "#424242",
                            "&:hover": {
                                backgroundColor: "#bdbdbd",
                            },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={handleConfirmDelete}
                        sx={{
                            backgroundColor: "#d32f2f",
                            color: "white",
                            "&:hover": {
                                backgroundColor: "#b71c1c",
                            },
                        }}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <div className="payment-back-container">
                <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
                <h2 className="payment-back">Back</h2>
            </div>

            <hr className="title-separator" />

            <div className="payment-header-title">
                <DocumentScannerSharpIcon />
                <h2 className="payment-title">
                    Procurement Invoice
                </h2>
                {isCreditNote && (
                    <span style={{
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                        padding: "2px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        marginLeft: "10px",
                    }}>
                        CREDIT NOTE
                    </span>
                )}
            </div>

            <div className="container-invoice-status">
                <div className="payment-input-text">{formData.invoice_number || "-"}</div>

                <div className="status-and-edit-group">
                    <div
                        style={{
                            backgroundColor: statusColors[formData.payments_status] || "#ccc",
                            color: "white",
                            padding: "5px 10px",
                            borderRadius: "10px",
                            width: "90px",
                            textAlign: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "15px",
                            marginRight: "8px",
                        }}
                    >
                        {formData.payments_status || "-"}
                    </div>

                    {canEditInvoice && (
                        <button
                            className="edit-button"
                            onClick={() =>
                                navigate(`/invoice-procurement/edit-invoice-procurement/${id}`)
                            }
                        >
                            <EditNoteSharpIcon
                                style={{ fontSize: "18px" }}
                                sx={{ color: "#1976d2" }}
                            />
                            Edit Invoice
                        </button>
                    )}
                </div>
            </div>

            <div className="payment-client-container">
                <div className="header">
                    <div className="supplier-info">
                        <BusinessIcon />
                        {formData.supplier_display || "N/A"}
                    </div>

                    {canCreatePayment && !isCreditNote && (
                        <div className="add-payment-button-large">
                            <Button
                                className="custom-button"
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon className="add-icon" />}
                                onClick={handleAddClick}
                            >
                                Add New payment
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <hr className="supplier-separator" />

            <div className="invoice-container">
                <div className="invoice-card">
                    <div className="invoice-grid">
                        {/* 1. Project code */}
                        <div className="invoice-label">Project code</div>
                        <div className="invoice-value">
                            {formData.project_code_display || "-"}
                        </div>

                        {/* 2. Subproject code */}
                        <div className="invoice-label">Subproject code</div>
                        <div className="invoice-value">
                            {formData.subproject_code_display || "-"}
                        </div>

                        {/* 3. Contract / Purchase Order */}
                        {formData.contract && (
                            <>
                                <div className="invoice-label">Contract</div>
                                <div className="invoice-value">
                                    {formData.contract_display || "-"}
                                </div>
                            </>
                        )}

                        {formData.purchase_order && (
                            <>
                                <div className="invoice-label">Purchase Order</div>
                                <div className="invoice-value">
                                    {formData.purchase_order_display || "-"}
                                </div>
                            </>
                        )}

                        {/* 4. Cost ID */}
                        <div className="invoice-label">Cost ID</div>
                        <div className="invoice-value">
                            {formData.cost_display || "-"}
                        </div>

                        {/* 5. Original Invoice - DOAR pentru Credit Note */}
                        {isCreditNote && (
                            <>
                                <div className="invoice-label">Original Invoice</div>
                                <div className="invoice-value">
                                    {formData.original_invoice_display || "-"}
                                </div>
                            </>
                        )}

                        {/* 6. Invoice without VAT */}
                        <div className="invoice-label">Invoice without VAT</div>
                        <div className="invoice-value">
                            {formatConvertedMoney(
                                formData.invoice_without_vat,
                                formData.invoice_without_vat_system
                            )}
                        </div>

                        {/* 7. Total invoice with VAT */}
                        <div className="invoice-label">Total invoice with VAT</div>
                        <div className="invoice-value">
                            {formatConvertedMoney(
                                formData.total_invoice_with_vat,
                                formData.total_invoice_with_vat_system
                            )}
                        </div>

                        {/* 8. Invoice date */}
                        <div className="invoice-label">Invoice date</div>
                        <div className="invoice-value">
                            {formData.invoice_date
                                ? format(new Date(formData.invoice_date), "dd.MM.yyyy")
                                : "-"}
                        </div>

                        {/* 9. Bill Maturity */}
                        <div className="invoice-label">Bill Maturity</div>
                        <div className="invoice-value">
                            {formData.bill_maturity
                                ? format(new Date(formData.bill_maturity), "dd.MM.yyyy")
                                : "-"}
                        </div>

                        {/* 10. Credit Note Without VAT - DOAR pentru Credit Note */}
                        {isCreditNote && (
                            <>
                                <div className="invoice-label">Credit Note Without VAT</div>
                                <div className="invoice-value">
                                    {formatConvertedMoney(
                                        formData.credit_note_without_vat,
                                        formData.credit_note_without_vat_system
                                    )}
                                </div>
                            </>
                        )}

                        {/* 11. Supplier VAT (%) */}
                        <div className="invoice-label">Supplier VAT (%)</div>
                        <div className="invoice-value">
                            {formatNumber(formData.supplier_vat || "-")} %
                        </div>

                        {/* 12. Credit Note With VAT - DOAR pentru Credit Note */}
                        {isCreditNote && (
                            <>
                                <div className="invoice-label">Credit Note With VAT</div>
                                <div className="invoice-value">
                                    {formatConvertedMoney(
                                        formData.credit_note_with_vat,
                                        formData.credit_note_with_vat_system
                                    )}
                                </div>
                            </>
                        )}

                        {/* 13. Difference Without VAT - DOAR pentru Credit Note */}
                        {isCreditNote && (
                            <>
                                <div className="invoice-label">Difference Without VAT</div>
                                <div className="invoice-value">
                                    {formatConvertedMoney(
                                        formData.difference_without_vat,
                                        formData.difference_without_vat_system
                                    )}
                                </div>
                            </>
                        )}

                        {/* 14. Difference With VAT - DOAR pentru Credit Note */}
                        {isCreditNote && (
                            <>
                                <div className="invoice-label">Difference With VAT</div>
                                <div className="invoice-value">
                                    {formatConvertedMoney(
                                        formData.difference_with_vat,
                                        formData.difference_with_vat_system
                                    )}
                                </div>
                            </>
                        )}

                        {/* 15. Month */}
                        <div className="invoice-label">Month</div>
                        <div className="invoice-value">{formData.month || "-"}</div>

                        {/* 16. Year */}
                        <div className="invoice-label">Year</div>
                        <div className="invoice-value">{formData.year || "-"}</div>

                        {/* 17. Remarks */}
                        <div className="invoice-label">Remarks</div>
                        <div className="invoice-value">{formData.remarks || "-"}</div>

                        {/* Attachments */}
                        <div
                            className="invoice-label attachments-label"
                            style={{
                                gridColumn: "1 / -1",
                                marginTop: "20px",
                                borderTop: "2px solid #ccc",
                                paddingTop: "20px",
                                marginBottom: "10px",
                            }}
                        >
                            <strong
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    fontSize: "16px",
                                }}
                            >
                                <AttachFileIcon /> Attachments ({existingAttachments.length})
                            </strong>
                        </div>

                        {loadingAttachments ? (
                            <div className="invoice-value" style={{ gridColumn: "1 / -1" }}>
                                Loading attachments...
                            </div>
                        ) : existingAttachments.length > 0 ? (
                            <div className="invoice-value" style={{ gridColumn: "1 / -1" }}>
                                <div className="files-list-view">
                                    {existingAttachments.map((attachment, index) => {
                                        const fileUrl = getFileUrl(attachment);

                                        return (
                                            <div
                                                key={attachment.id || index}
                                                className="file-preview-item-view"
                                                onClick={() => handleFileClick(attachment)}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    padding: "8px 16px",
                                                    margin: "0 8px 8px 0",
                                                    backgroundColor: "#f5f5f5",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    border: "1px solid #e0e0e0",
                                                    transition: "all 0.2s ease",
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = "#e8e8e8";
                                                    e.currentTarget.style.borderColor = "#1976d2";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = "#f5f5f5";
                                                    e.currentTarget.style.borderColor = "#e0e0e0";
                                                }}
                                                title={
                                                    fileUrl ? "Click to open file" : "File URL not available"
                                                }
                                            >
                                                {getFileIcon(attachment)}

                                                <span className="file-name" style={{ fontWeight: "500" }}>
                                                    {attachment.filename?.length > 30
                                                        ? attachment.filename.substring(0, 27) + "..."
                                                        : attachment.filename || "Unknown file"}
                                                </span>

                                                {attachment.file_size > 0 && (
                                                    <span
                                                        className="file-size"
                                                        style={{
                                                            fontSize: "11px",
                                                            color: "#666",
                                                            fontWeight: "normal",
                                                        }}
                                                    >
                                                        ({(attachment.file_size / 1024).toFixed(1)} KB)
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div
                                className="invoice-value"
                                style={{
                                    gridColumn: "1 / -1",
                                    color: "#999",
                                    fontStyle: "italic",
                                    padding: "10px 0",
                                }}
                            >
                                No attachments
                            </div>
                        )}
                    </div>
                </div>

                {canCreatePayment && !isCreditNote && (
                    <div className="add-payment-button-small">
                        <Button
                            className="custom-button"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon className="add-icon" />}
                            onClick={handleAddClick}
                        >
                            Add New payment
                        </Button>
                    </div>
                )}

                <div className="invoice-card">
                    <div className="payment-ext-resource-invoice-page">
                        {invoicePayments.length > 0 && (
                            <div className="invoice-card">
                                <div className="payment-grid">
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <tbody>
                                            {[...invoicePayments]
                                                .sort((a, b) => a.id - b.id)
                                                .map((payment, index) => (
                                                    <tr key={index}>
                                                        <td style={{ fontWeight: "bold" }}>Payment</td>

                                                        <td>
                                                            {formatConvertedMoney(
                                                                payment.supplier_payment,
                                                                payment.supplier_payment_system_display ||
                                                                payment.supplier_payment_system ||
                                                                payment.amount_system_display ||
                                                                payment.amount_system ||
                                                                ""
                                                            )}
                                                        </td>

                                                        <td>
                                                            {format(
                                                                new Date(payment.invoice_pay_date),
                                                                "dd.MM.yyyy"
                                                            )}
                                                        </td>

                                                        {(canEditPayment || canDeletePayment) && (
                                                            <td>
                                                                {canEditPayment && (
                                                                    <IconButton
                                                                        sx={{ color: "#1976d2" }}
                                                                        onClick={() => handleEditClick(payment)}
                                                                    >
                                                                        <EditNoteSharpIcon />
                                                                    </IconButton>
                                                                )}

                                                                {canDeletePayment && (
                                                                    <IconButton
                                                                        onClick={() => handleDeleteClick(payment)}
                                                                        sx={{ color: "#d32f2f" }}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                )}
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {!isCreditNote && (
                            <div className="invoice-card">
                                <div className="invoice-grid-payment">
                                    <div className="payment-remaining">
                                        <strong>Remaining payment: </strong>{" "}
                                        {formatConvertedMoney(
                                            remainingAmount,
                                            formData.remaining_payment_system_display ||
                                            formData.remaining_payment_system
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentInvoiceProcurement;